import { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Plus, Trash2, Info, Mail } from 'lucide-react';
import { Button, Modal, InfoAlert } from '@/components/ui';
import { SenderCombobox } from '@/components/domain/shared/SenderCombobox';
import { COMMON_SENDER_PLATFORMS } from '@/constants/senderPlatforms';
import { RequiredSenderBadge } from '@/components/domain/shared/RequiredSenderBadge';
import { SettingsSubPage } from '@/pages/settings/SettingsSubPage';
import { toast } from '@/stores';
import { settingsService } from '@/services';
import { useAppShell, useGmailConnectionStatus, useUserRole } from '@/hooks';
import { formatDomainDisplay, validateSender } from '@/lib/senderHelpers';
import { resyncGmailFilters as resyncGmailFiltersService } from '@/services/gmail-forwarding.service';
import {
  MIN_SEARCH_LENGTH,
  fetchRecentSenders,
  searchHouseholdSenders,
} from '@/services/sender-search.service';
import type { SuggestedSender } from '@/pages/onboarding/StepAddSenders';
import { SettingsApprovedSendersSkeleton } from './skeletons';
import type { ApprovedSender } from '@/services/settings.service';

export function SettingsApprovedSenders() {
  const { householdId, householdResolved } = useAppShell();
  const { isPrimary, roleResolved } = useUserRole();
  const { data: gmailStatus } = useGmailConnectionStatus(householdId ?? null);
  const [senders, setSenders] = useState<ApprovedSender[]>([]);
  const [loading, setLoading] = useState(true);
  const [newValue, setNewValue] = useState('');
  const [inputError, setInputError] = useState<string | undefined>();
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ApprovedSender | null>(null);
  // Live mailbox matches for the typed term (YAR-502). `null` = no usable
  // Gmail grant, so the dropdown falls back to the curated platform list.
  const [liveMatches, setLiveMatches] = useState<SuggestedSender[] | null>([]);
  // Senders of recent mail, fetched once; SenderCombobox matches them by
  // substring as the parent types (YAR-374). The mailbox search matches whole
  // words, so "merc" alone never reached `@mercadolibre.com`.
  const [recentSenders, setRecentSenders] = useState<SuggestedSender[]>([]);

  const loadSenders = useCallback(async (hid: string) => {
    const data = await settingsService.getApprovedSenders(hid);
    setSenders(data);
  }, []);

  // Brandon and Kelvin reported: senders added in Settings were written
  // to the DB but never appeared as Gmail filters routing to the Yaruta
  // label, so no emails from those senders reached the app. Onboarding's
  // StepApproveSenders does this resync inline using the fresh OAuth
  // provider_token — but a user returning to Settings later has lost
  // that token (~1h TTL in supabase.auth.session), so a client-side call
  // to setupGmailForwarding would silently no-op.
  //
  // Fix: hand the work to gmail-filters-resync, which uses the
  // household's stored refresh_token server-side and recreates the
  // filter set from `approved_senders` whenever this user edits the
  // list. needs_oauth=true means the stored refresh_token is gone or
  // revoked — surface the reconnect hint.
  const resyncGmailFilters = useCallback(
    async (hid: string) => {
      if (gmailStatus?.state !== 'connected') return;
      try {
        const result = await resyncGmailFiltersService(hid);
        if (!result.ok && result.needs_oauth) {
          toast.default(
            'Reconnect Gmail to apply the new sender list to your inbox',
          );
        } else if (!result.ok) {
          console.warn(
            '[SettingsApprovedSenders] Filter resync failed:',
            result.errors,
          );
        }
      } catch (err) {
        console.warn(
          '[SettingsApprovedSenders] Filter resync threw:',
          err instanceof Error ? err.message : err,
        );
      }
    },
    [gmailStatus?.state],
  );

  useEffect(() => {
    if (!householdId) {
      // Only stop showing the skeleton once resolution has FINISHED.
      // Otherwise the first paint (id still pending) renders this page's
      // empty/default state before the real data can possibly arrive.
      if (householdResolved) setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await loadSenders(householdId);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [householdId, householdResolved, loadSenders]);

  // Derived, not stored: recomputing on each keystroke keeps the notice in
  // step with the input without a second piece of state to forget to clear.
  const addWarning = newValue.trim()
    ? validateSender(newValue).warning
    : undefined;

  const handleAdd = async () => {
    if (!householdId) return;
    const validation = validateSender(newValue);
    if (!validation.valid || !validation.storageValue) {
      setInputError(validation.error);
      return;
    }

    // Dedupe against existing entries (case-insensitive, normalized).
    const exists = senders.some(
      (s) =>
        s.type === validation.type &&
        s.value.toLowerCase() === validation.storageValue!.toLowerCase(),
    );
    if (exists) {
      setInputError('This sender has already been added');
      return;
    }

    setAdding(true);
    try {
      await settingsService.addApprovedSender(
        householdId,
        validation.type,
        validation.storageValue,
      );
      setNewValue('');
      setInputError(undefined);
      await loadSenders(householdId);
      toast.success('Sender added');
      await resyncGmailFilters(householdId);
    } catch (err) {
      console.error('Failed to add approved sender:', err);
      toast.error('Failed to add sender');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !householdId) return;
    try {
      await settingsService.removeApprovedSender(deleteTarget.id);
      setDeleteTarget(null);
      await loadSenders(householdId);
      toast.success('Sender removed');
      await resyncGmailFilters(householdId);
    } catch (err) {
      console.error('Failed to remove approved sender:', err);
      toast.error('Failed to remove sender');
    }
  };

  // Debounced live search of the parent's own mailbox (YAR-502). The
  // dropdown used to offer only a hardcoded platform list (ClassDojo,
  // PowerSchool…) whatever was typed. The provider_token is gone by the time
  // a parent returns to Settings, so the edge function searches with the
  // household's stored grant instead — the same one gmail-filters-resync
  // uses. A failure clears the matches rather than inventing any.
  useEffect(() => {
    const q = newValue.trim();
    if (!householdId || q.length < MIN_SEARCH_LENGTH) {
      setLiveMatches((prev) => (prev === null ? prev : []));
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchHouseholdSenders(householdId, q)
        .then(({ senders: rows, needsOauth }) => {
          if (!cancelled) setLiveMatches(needsOauth ? null : rows);
        })
        .catch((err) => {
          if (cancelled) return;
          console.warn(
            '[SettingsApprovedSenders] Sender search failed:',
            err instanceof Error ? err.message : err,
          );
          setLiveMatches([]);
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [newValue, householdId]);

  useEffect(() => {
    if (!householdId) return;
    let cancelled = false;
    fetchRecentSenders({ householdId })
      .then((rows) => {
        if (!cancelled) setRecentSenders(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  // Type-ahead options, minus anything already approved: senders found in
  // the parent's mailbox. Only with no usable Gmail grant does it fall back
  // to the curated platform list, so the field still helps a parent who has
  // to reconnect.
  const senderOptions = useMemo(() => {
    const approved = new Set(
      senders.map((s) =>
        (s.type === 'domain'
          ? formatDomainDisplay(s.value)
          : s.value
        ).toLowerCase(),
      ),
    );
    // `matchedBy` is not carried over on purpose: SenderCombobox filters
    // options by the typed text itself, so a sender the search matched only
    // on message CONTENT (YAR-374) never shows here. Onboarding's dropdown
    // lists those rows with an explanation; this combobox has no slot for
    // one yet.
    const toOption = (m: SuggestedSender) => ({
      value: m.isDomain ? formatDomainDisplay(m.value) : m.value,
      label: m.displayName,
    });
    // Recent senders go first: they carry the prefix matches (a domain row
    // as soon as "merc" is typed) the whole-word mailbox search cannot.
    const options =
      liveMatches === null
        ? COMMON_SENDER_PLATFORMS
        : [...recentSenders, ...liveMatches].map(toOption);
    const seen = new Set<string>();
    return options.filter((o) => {
      const key = (typeof o === 'string' ? o : o.value).toLowerCase();
      if (approved.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [senders, liveMatches, recentSenders]);

  // Defense-in-depth: SettingsMain hides the nav link for non-PRIMARY
  // members, but the route is still reachable by URL. Mirror the
  // SettingsPlan guard so only the PRIMARY can mutate the household's
  // approved-sender list.
  if (roleResolved && !isPrimary) {
    return (
      <Navigate
        to="/settings"
        replace
      />
    );
  }

  return (
    <SettingsSubPage title="Approved Senders">
      {loading ? (
        <SettingsApprovedSendersSkeleton />
      ) : (
        <div className="ph-no-capture flex flex-col gap-6 px-5 py-5">
          {/* Info banner */}
          <InfoAlert icon={<Info size={16} />}>
            Only emails from these addresses or domains will be processed.
            Everything else is ignored.
          </InfoAlert>

          {/* Add sender input */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              Domain or email address
            </label>
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">
                <SenderCombobox
                  value={newValue}
                  onChange={(next) => {
                    setNewValue(next);
                    if (inputError) setInputError(undefined);
                  }}
                  onSubmit={handleAdd}
                  options={senderOptions}
                  placeholder="e.g. @school.org or teacher@email.com"
                  ariaLabel="Domain or email address"
                />
              </div>
              <Button
                variant="secondary"
                leftIcon={<Plus size={16} />}
                onClick={handleAdd}
                disabled={adding || !newValue.trim()}
              >
                Add
              </Button>
            </div>
            {inputError && <p className="text-xs text-danger">{inputError}</p>}
            {/* Breadth notice for a sender that IS addable but is broader than
                it looks — the same thing onboarding states rather than hiding.
                Shown while typing, not after adding, so it can still change
                the parent's mind. Suppressed while an error is on screen so
                the two never stack. */}
            {!inputError && addWarning && (
              <p className="text-xs text-muted-foreground">{addWarning}</p>
            )}
          </div>

          {/* Senders list. The heading renders even with nothing in the
              list: an empty screen used to collapse to just the info alert
              and the add field, with no sign that the list exists at all
              (YAR-230). Figma's "Approved senders / empty" frame
              (8508:50755) keeps the heading and fills the list area with
              the panel below. */}
          <div className="flex flex-col gap-2">
            <h2 className="text-sm font-medium text-foreground">
              Approved senders
            </h2>
            {senders.length === 0 ? (
              <div
                role="status"
                className="rounded-xl bg-surface-secondary p-6 flex flex-col items-center justify-center gap-2 min-h-[180px] text-center"
              >
                <Mail
                  size={32}
                  className="text-icon-default"
                  aria-hidden="true"
                />
                <p className="text-base font-medium text-foreground">
                  No senders found yet
                </p>
                <p className="text-sm text-muted-foreground">
                  You can add senders manually.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {senders.map((sender) => {
                  const display =
                    sender.type === 'domain'
                      ? formatDomainDisplay(sender.value)
                      : sender.value;
                  // system_managed rows (e.g. user's own Gmail seeded by
                  // gmail-forwarding-setup) can't be deleted via the UI —
                  // they're owned by the system and RLS rejects DELETE.
                  // Show a "Required" badge whose tooltip explains the
                  // constraint instead of offering a trash icon that
                  // would 403.
                  return (
                    <div
                      key={sender.id}
                      className="flex items-center justify-between gap-3 border border-border rounded-lg px-4 py-3"
                    >
                      {/* min-w-0 lets the text shrink inside the flex row and
                          break-all wraps an address with no break points, so a
                          long sender (YAR-486, 46 chars) stays inside the card
                          instead of pushing the trash icon off-screen. */}
                      <span className="min-w-0 break-all text-sm text-foreground">
                        {display}
                      </span>
                      {sender.system_managed ? (
                        <RequiredSenderBadge
                          ariaLabel={`${display} — required sender, used to accept forwarded emails`}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(sender)}
                          className="shrink-0 text-muted-foreground hover:text-danger transition-colors cursor-pointer"
                          aria-label={`Delete ${display}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        bare
      >
        <div className="ph-no-capture flex flex-col gap-4 items-center p-5">
          <div className="flex flex-col gap-1.5 w-full text-center">
            <p className="text-xl font-medium text-foreground">
              Delete{' '}
              {/* break-all on the address only: it has no break points, so
                  without it a long sender runs past the modal's edge
                  (YAR-487); "Delete" itself must never split. */}
              <span className="break-all">
                {deleteTarget
                  ? deleteTarget.type === 'domain'
                    ? formatDomainDisplay(deleteTarget.value)
                    : deleteTarget.value
                  : ''}
                ?
              </span>
            </p>
            <p className="text-base text-foreground">
              New emails from this sender will no longer be processed.
            </p>
          </div>
          <div className="flex flex-col gap-3 w-full">
            <button
              type="button"
              onClick={handleDelete}
              className="w-full border border-danger text-danger font-semibold text-base leading-6 py-1.5 px-2 rounded-md cursor-pointer"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              className="w-full text-muted-foreground font-semibold text-base leading-6 py-1.5 px-2 cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </SettingsSubPage>
  );
}
